/**
 * Who is signed in. One context, read by the shell, the role gates and every page.
 *
 * **Signing in or out empties the query cache**, and that is a privacy control rather than
 * housekeeping. The API redacts a phone number per REQUEST, by the role on the session — so the
 * rows an owner fetched carry numbers, and they sit in react-query's cache keyed only by
 * `['inbox', …]`. Sign out, sign back in as `ops` in the same tab, and every page would render
 * from that cache: the owner's numbers, on an ops screen, with no request made that the server
 * could have refused. Ruling 4 is enforced at the API and it would have been walked around here.
 *
 * Author: Danish Khan
 */
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api.ts';
import type { Session } from './types.ts';

interface SessionState {
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    void api
      .me()
      .then((s) => {
        if (live) setSession(s);
      })
      .catch(() => {
        if (live) setSession(null);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const queryClient = useQueryClient();

  const signIn = useCallback(
    async (email: string, password: string) => {
      const next = await api.login(email, password);
      // Before the new session is published, so no page can render one frame of the previous
      // role's rows. `clear()` and not `invalidateQueries()`: invalidation leaves the stale data
      // in place and shows it while the refetch is in flight, which is the whole leak.
      queryClient.clear();
      setSession(next);
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    await api.logout();
    setSession(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<SessionState>(
    () => ({ session, loading, signIn, signOut }),
    [session, loading, signIn, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

/** The signed-in user, for pages that only render behind the auth gate. */
export function useUser(): Session['user'] {
  const { session } = useSession();
  if (!session) throw new Error('useUser outside an authenticated route');
  return session.user;
}

/**
 * The tenant on the session — its name, its timezone, and what its agent is called.
 *
 * `GET /auth/me` fills `agentName` from the tenant's own `tenant.yaml` in its pack. Screens that
 * address the agent by name read it from here rather than writing "Maya" into the JSX: for this
 * tenant the two are the same string, and for the second tenant only one of them is.
 */
export function useTenant(): Session['tenant'] {
  const { session } = useSession();
  if (!session) throw new Error('useTenant outside an authenticated route');
  return session.tenant;
}
